import type { StaticProductView } from 'state/product-description/types';
import { SalesDesk } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import { isFeatureEnabled } from 'state/selectors/common/userFeatures';
import type { AppState } from 'state/state.model';

import type { UserState } from './user.model';

function selectUserState(state: AppState): UserState {
  return state.user;
}

export function hasUserPermission(state: AppState, permission: Permission) {
  return state.user.permissions?.includes(permission) ?? false;
}

export function isUserInternal(state: UserState): boolean {
  return state.isInternal;
}

export function isTraderUser(state: UserState): boolean | undefined {
  return state.isTrader;
}

export function getUserCompanyBdrId(state: UserState): string {
  return state.bdrId;
}

export function getUserCompanyName(state: UserState): string {
  return state.companyName;
}

export function getDefaultProductView(state: UserState): StaticProductView {
  return state.defaultStaticProductView ?? 'Default';
}

export function isTradeCancellationEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return (
    isFeatureEnabled(appState, 'tradeCancellation') && state.isInternal && isProductModificationEnabledForUser(appState)
  );
}

export function isComputeVolStoEnabled(appState: AppState): boolean {
  const state = selectUserState(appState);
  return Boolean(state.entitlements && state.entitlements.computeVolStoEnabled);
}

export function isDynamicRepriceEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return Boolean(state.entitlements && state.entitlements.dynamicRepriceEnabled);
}

export function isAddonEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  const isInternal = isUserInternal(state);
  return Boolean(state.entitlements && state.entitlements.quoteEnabled && isInternal);
}

export function isTradeAllocationEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return Boolean(state.entitlements && state.entitlements.tradeAllocationEnabled);
}

export function isTradeAllocationApplicableForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return Boolean(state.entitlements && state.entitlements.tradeAllocationApplyable);
}

export function isProductModificationEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return Boolean(state.entitlements && state.entitlements.productModificationEnabled);
}

export function isProductModificationApplicableForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return Boolean(state.entitlements && state.entitlements.productModificationApplyable);
}

export function isSecondaryMarketTradeEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.secondaryTradeEnabled ?? false;
}

export function isSecondaryMarketCpOrderEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.secondaryCpOrderEnabled ?? false;
}

export function isSecondaryOrderBookEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.secondaryOrderBookEnabled ?? false;
}

export function isSecondaryMarketLiveQuoteEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.realTimeScreenEnabled ?? false;
}

export function isSecondaryMarketLimitOrderEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.limitOrderScreenEnabled ?? false;
}

export function isSecondaryMarketAmendLiveTradeEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.amendLiveTradeEnabled ?? false;
}

function areUserInfosLoaded(appState: AppState): boolean {
  return selectUserState(appState).entitlements !== undefined;
}
export function isApplicationReady(state: AppState): boolean {
  return areUserInfosLoaded(state);
}

export function isTradeEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.tradeEnabled ?? false;
}

export function getUserLogin(appState: AppState): string {
  return selectUserState(appState).login;
}

export function getAppFeedbackUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.appFeedbackUrl;
}

export function getContacts(appState: AppState) {
  return selectUserState(appState).environmentDetails.contacts;
}

export function getDiscoveryUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.discoveryUrl;
}

export function getInsightPortalUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.insightPortalUrl;
}

export function getLifecyclePortalUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.lifecyclePortalUrl;
}

export function getMacroMattersUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.macroMattersUrl;
}

export function getSplabUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.splabUrl;
}

export function getSplabPricingUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.splabPricingUrl;
}

export function getThematicInsightsUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails.thematicInsightsUrl;
}

export function getSparkUrl(appState: AppState): string {
  return selectUserState(appState).environmentDetails?.sparkUrl;
}

export function getSparkUrlByFileCode(appState: AppState, fileCode?: string): string | undefined {
  return fileCode ? selectUserState(appState).environmentDetails?.sparkUrl + fileCode : undefined;
}

export function isDiscoverPageEnabledForUser(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.discoverPageEnabled ?? false;
}
export function hasPrimaryQuote(appState: AppState): boolean {
  const state = selectUserState(appState);
  return state.entitlements?.quoteEnabled ?? false;
}
const emptyDesks: SalesDesk[] = [];
export function getUserSalesDesks(appState: AppState) {
  return appState.user.salesDesks ?? emptyDesks;
}
